import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import Swal from 'sweetalert2';
import './EmployeeManagement.css';
import { Modal, Button } from 'react-bootstrap';

// Helper to format dates to YYYY-MM-DD for <input type="date" />
const toDateInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);

  // filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dobFrom, setDobFrom] = useState('');
  const [dobTo, setDobTo] = useState('');
  const [joiningFrom, setJoiningFrom] = useState('');
  const [joiningTo, setJoiningTo] = useState('');

  // modal & file states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const initialForm = {
    name: '',
    gender: '',
    dob: '',
    phone: '',
    email: '',
    aadhaar_number: '',
    pan_number: '',
    educational_qualification: '',
    professional_qualification: '',
    experience_years: '',
    blood_group: '',
    emergency_contact: '',
    marital_status: '',
    bank_account_number: '',
    ifsc_code: '',
    bank_name: '',
    account_holder_name: '',
    department_id: '',
    designation: '',
    joining_date: '',
    address: '',
    status: 'enabled',
  };

  const [form, setForm] = useState(initialForm);

  const getBackendError = (err) =>
    err.response?.data?.error ||
    err.response?.data?.message ||
    err.message ||
    'Operation failed';

  // SweetAlert helper: keeps alerts above Bootstrap modal/backdrop/page layout.
  const fireAlert = (arg1, arg2, arg3) => {
    const baseOptions = {
      target: document.body,
      heightAuto: false,
      customClass: {
        container: 'erp-swal-container',
      },
      didOpen: (popup) => {
        const container = Swal.getContainer();
        if (container) {
          container.style.zIndex = '2147483000';
        }
      },
    };

    if (typeof arg1 === 'object') {
      const userDidOpen = arg1.didOpen;

      return Swal.fire({
        ...baseOptions,
        ...arg1,
        customClass: {
          ...baseOptions.customClass,
          ...(arg1.customClass || {}),
          container: `erp-swal-container ${arg1.customClass?.container || ''}`.trim(),
        },
        didOpen: (popup) => {
          baseOptions.didOpen(popup);
          if (typeof userDidOpen === 'function') {
            userDidOpen(popup);
          }
        },
      });
    }

    return Swal.fire({
      ...baseOptions,
      title: arg1,
      text: arg2,
      icon: arg3,
    });
  };

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, []);

  const fetchEmployees = async () => {
    try {
      const { data } = await api.get('/employees');
      setEmployees(data.employees || []);
    } catch {
      fireAlert('Error', 'Failed to load employees', 'error');
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data } = await api.get('/departments');
      setDepartments(data.departments || []);
    } catch {
      fireAlert('Error', 'Failed to load departments', 'error');
    }
  };

  const openModal = (mode, emp = null) => {
    setModalMode(mode);

    if (mode === 'edit' && emp) {
      setForm({
        ...initialForm,
        ...emp,
        department_id: emp.department_id ? String(emp.department_id) : '',
        dob: toDateInput(emp.dob),
        joining_date: toDateInput(emp.joining_date),
      });
      setCurrentEmployee(emp);
    } else {
      setForm(initialForm);
      setCurrentEmployee(null);
    }

    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submitForm = async (e) => {
    e.preventDefault();

    for (const field of ['name', 'phone', 'email']) {
      if (!form[field]) {
        return fireAlert(
          'Validation',
          `Field "${field.replace(/_/g, ' ')}" is required`,
          'warning'
        );
      }
    }

    try {
      const cleanedForm = {};

      for (const [key, value] of Object.entries(form)) {
        cleanedForm[key] = value === '' ? null : value;
      }

      if (cleanedForm.department_id) {
        cleanedForm.department_id = Number(cleanedForm.department_id);
      }

      if (modalMode === 'add') {
        await api.post('/employees', cleanedForm);
      } else {
        await api.put(`/employees/${currentEmployee.id}`, cleanedForm);
      }

      closeModal();

      fireAlert(
        'Success',
        `Employee ${modalMode === 'add' ? 'added' : 'updated'}`,
        'success'
      );

      fetchEmployees();
    } catch (err) {
      fireAlert('Error', getBackendError(err), 'error');
    }
  };

  const toggleStatus = async (emp) => {
    const action = emp.status === 'enabled' ? 'Disable' : 'Enable';

    const res = await fireAlert({
      title: `${action} Employee?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: action,
      cancelButtonText: 'Cancel',
    });

    if (!res.isConfirmed) return;

    try {
      await api.put(`/employees/${emp.id}`, {
        status: emp.status === 'enabled' ? 'disabled' : 'enabled',
      });

      fireAlert('Success', `Employee ${action.toLowerCase()}d`, 'success');
      fetchEmployees();
    } catch (err) {
      fireAlert('Error', getBackendError(err), 'error');
    }
  };

  const handleExportTemplate = async () => {
    try {
      const response = await api.get('/employees/export-template', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'employee_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      fireAlert('Success', 'Template downloaded successfully', 'success');
    } catch {
      fireAlert('Error', 'Failed to download template', 'error');
    }
  };

  const handleExportData = async () => {
    try {
      const params = new URLSearchParams();

      if (searchTerm) params.append('search', searchTerm);
      if (selectedDept) params.append('department_id', selectedDept);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (dobFrom) params.append('dob', dobFrom);
      if (dobTo) params.append('dobTo', dobTo);
      if (joiningFrom) params.append('joining_date', joiningFrom);
      if (joiningTo) params.append('joiningDateTo', joiningTo);

      const url = `/employees/export${params.toString() ? `?${params}` : ''}`;

      const response = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');

      a.href = blobUrl;
      a.download = 'employees_export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      fireAlert('Success', 'All employees exported', 'success');
    } catch {
      fireAlert('Error', 'Failed to export data', 'error');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
  };

  const handleImport = async () => {
    if (!file) {
      return fireAlert('Error', 'Please select a file to import', 'warning');
    }

    const allowedExtensions = ['xlsx', 'xls'];
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      return fireAlert('Error', 'Please select a valid Excel file (.xlsx or .xls)', 'warning');
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Important:
      // Do NOT manually set Content-Type here.
      // Axios/browser will set correct multipart boundary automatically.
      const { data } = await api.post('/employees/import', formData);

      const skippedRowsHtml =
        data.skippedRows?.length > 0
          ? `
            <hr />
            <p><b>Skipped Rows:</b> ${data.skippedCount || data.skippedRows.length}</p>
            <div style="text-align:left; max-height:180px; overflow:auto;">
              ${data.skippedRows
                .slice(0, 20)
                .map(
                  (item) =>
                    `<p style="margin:4px 0;">Row ${item.row}: ${item.reason}</p>`
                )
                .join('')}
            </div>
          `
          : '';

      fireAlert({
        title: 'Import Complete',
        html: `
          <p>Inserted: ${data.insertedCount || 0} employees</p>
          <p>Duplicates: ${data.duplicateCount || 0}</p>
          <p>Skipped: ${data.skippedCount || 0}</p>
          ${
            data.duplicates?.length > 0
              ? `<p><b>Duplicate Aadhaar Numbers:</b> ${data.duplicates.join(', ')}</p>`
              : ''
          }
          ${skippedRowsHtml}
        `,
        icon: 'success',
        width: 650,
      });

      setFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      fetchEmployees();
    } catch (err) {
      fireAlert('Error', getBackendError(err), 'error');
    }
  };

  const designations = [
    ...new Set(employees.map((emp) => emp.designation).filter(Boolean)),
  ];

  const displayed = employees.filter((emp) => {
    if (statusFilter !== 'all' && emp.status !== statusFilter) return false;

    if (
      searchTerm &&
      !String(emp.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }

    if (selectedDept && String(emp.department_id) !== selectedDept) return false;

    if (selectedDesignation && emp.designation !== selectedDesignation) {
      return false;
    }

    if (dobFrom && emp.dob && emp.dob < dobFrom) return false;
    if (dobTo && emp.dob && emp.dob > dobTo) return false;

    if (joiningFrom && emp.joining_date && emp.joining_date < joiningFrom) {
      return false;
    }

    if (joiningTo && emp.joining_date && emp.joining_date > joiningTo) {
      return false;
    }

    return true;
  });

  return (
    <div className="container-fluid px-0">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center my-4 flex-wrap">
        <h3>Employees</h3>

        <div className="d-flex gap-2 flex-wrap">
          <div className="btn-group me-3" role="group">
            <button
              type="button"
              onClick={handleExportTemplate}
              className="btn btn-outline-secondary"
            >
              Export Template
            </button>

            <button
              type="button"
              onClick={handleExportData}
              className="btn btn-secondary"
            >
              Export All Employees
            </button>
          </div>

          <div className="input-group" style={{ maxWidth: 260 }}>
            <input
              ref={fileInputRef}
              type="file"
              className="form-control"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
            />

            <button
              type="button"
              onClick={handleImport}
              className="btn btn-primary"
              disabled={!file}
            >
              Import
            </button>
          </div>

          <button
            type="button"
            onClick={() => openModal('add')}
            className="btn btn-primary"
          >
            Add Employee
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="d-flex gap-3 mb-2 flex-wrap">
        <input
          type="text"
          className="form-control w-auto"
          style={{ minWidth: 200 }}
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="form-select w-auto"
          style={{ minWidth: 200 }}
          value={selectedDept}
          onChange={(e) => setSelectedDept(e.target.value)}
        >
          <option value="">All Departments</option>
          {departments.map((dep) => (
            <option key={dep.id} value={dep.id}>
              {dep.name}
            </option>
          ))}
        </select>

        <select
          className="form-select w-auto"
          style={{ minWidth: 200 }}
          value={selectedDesignation}
          onChange={(e) => setSelectedDesignation(e.target.value)}
        >
          <option value="">All Designations</option>
          {designations.map((d, i) => (
            <option key={i} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          className="form-select w-auto"
          style={{ minWidth: 200 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="enabled">Active</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {/* DATE FILTERS */}
      <div className="d-flex gap-4 mb-4 flex-wrap">
        <div className="d-flex align-items-center gap-2">
          <label className="mb-0">Date of Birth:</label>

          <input
            type="date"
            className="form-control form-control-sm w-auto"
            value={dobFrom}
            onChange={(e) => setDobFrom(e.target.value)}
          />

          <span>to</span>

          <input
            type="date"
            className="form-control form-control-sm w-auto"
            value={dobTo}
            onChange={(e) => setDobTo(e.target.value)}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="mb-0">Joining Date:</label>

          <input
            type="date"
            className="form-control form-control-sm w-auto"
            value={joiningFrom}
            onChange={(e) => setJoiningFrom(e.target.value)}
          />

          <span>to</span>

          <input
            type="date"
            className="form-control form-control-sm w-auto"
            value={joiningTo}
            onChange={(e) => setJoiningTo(e.target.value)}
          />
        </div>
      </div>

      {/* EMPLOYEE TABLE */}
      <div className="table-responsive">
        <table className="table table-striped mb-0">
          <thead className="bg-light">
            <tr>
              <th>Emp ID</th>
              <th>Name</th>
              <th>Gender</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Dept</th>
              <th>Designation</th>
              <th>Joining Date</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {displayed.length > 0 ? (
              displayed.map((emp) => (
                <tr
                  key={emp.id}
                  className={emp.status === 'disabled' ? 'table-danger' : ''}
                >
                  <td>{emp.employee_id}</td>
                  <td>{emp.name}</td>
                  <td>{emp.gender}</td>
                  <td>{emp.phone}</td>
                  <td>{emp.email}</td>
                  <td>{emp.department?.name || emp.Department?.name || '-'}</td>
                  <td>{emp.designation || '-'}</td>
                  <td>{emp.joining_date || '-'}</td>
                  <td>
                    <button
                      type="button"
                      onClick={() => openModal('edit', emp)}
                      className="btn btn-sm btn-info me-1"
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleStatus(emp)}
                      className="btn btn-sm btn-warning"
                    >
                      {emp.status === 'enabled' ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-3">
                  No employees found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      <Modal
        show={modalOpen}
        onHide={closeModal}
        size="xl"
        centered
        scrollable
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'add' ? 'Add Employee' : 'Edit Employee'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form onSubmit={submitForm} className="row g-3">
            {Object.entries(initialForm)
              .filter(([k]) => k !== 'status')
              .map(([key]) => (
                <div
                  key={key}
                  className={`col-md-${key === 'address' ? 12 : 3}`}
                >
                  <label className="form-label text-capitalize">
                    {key.replace(/_/g, ' ')}

                    {[
                      'name',
                      'gender',
                      'dob',
                      'aadhaar_number',
                      'department_id',
                      'designation',
                      'joining_date',
                    ].includes(key) && <span className="text-danger"> *</span>}
                  </label>

                  {key === 'address' ? (
                    <textarea
                      name="address"
                      value={form.address || ''}
                      onChange={handleFormChange}
                      rows={3}
                      className="form-control"
                    />
                  ) : ['gender', 'marital_status', 'blood_group'].includes(
                      key
                    ) ? (
                    <select
                      name={key}
                      value={form[key] || ''}
                      onChange={handleFormChange}
                      className="form-select"
                    >
                      <option value="">Select</option>

                      {(key === 'gender'
                        ? ['Male', 'Female', 'Other']
                        : key === 'marital_status'
                        ? ['Single', 'Married', 'Other']
                        : ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']
                      ).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : key === 'department_id' ? (
                    <select
                      name="department_id"
                      value={form.department_id || ''}
                      onChange={handleFormChange}
                      className="form-select"
                    >
                      <option value="">Select Department</option>

                      {departments.map((dep) => (
                        <option key={dep.id} value={dep.id}>
                          {dep.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={
                        key === 'dob' || key === 'joining_date'
                          ? 'date'
                          : key === 'experience_years'
                          ? 'number'
                          : 'text'
                      }
                      name={key}
                      value={form[key] || ''}
                      onChange={handleFormChange}
                      className="form-control"
                    />
                  )}
                </div>
              ))}

            <div className="col-12 text-end">
              <Button
                variant="secondary"
                onClick={closeModal}
                className="me-2"
              >
                Cancel
              </Button>

              <Button type="submit">
                {modalMode === 'add' ? 'Add' : 'Update'}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EmployeeManagement;